import type { DragEvent, ReactNode } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ExtRenameInput } from "./ExtRenameInput";
import { ArrowOpenIcon, DownloadIcon } from "./icons";

export function DocItemRow({
  idPrefix,
  kind,
  name,
  when,
  source,
  sourceDisplay,
  isNew,
  isRenaming,
  selected,
  onToggleSelect,
  onStartRename,
  onRename,
  onStopRename,
  onOpen,
  onDownload,
  draggable,
  onDragStart,
  onDragEnd,
  disabled = false,
}: {
  idPrefix?: string;
  kind: string;
  name: string;
  when: string | null;
  source: string;
  sourceDisplay?: ReactNode;
  // Created within the recent window → small accent "new" dot.
  isNew?: boolean;
  // This row is currently in rename mode (only one row at a time, parent-driven).
  isRenaming: boolean;
  selected: boolean;
  onToggleSelect?: () => void;
  // Click the name → caller flips this row into rename mode.
  onStartRename?: () => void;
  onRename: (newName: string) => void;
  // Blur / Enter / Escape on the rename input → caller exits rename mode.
  onStopRename: () => void;
  onOpen: () => void;
  // undefined for CV drafts - their download lives in the editor's previewer.
  onDownload?: () => void;
  // Drag & drop: real documents can be dragged onto folders to move them.
  draggable?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
  disabled?: boolean;
}) {
  const intl = useIntl();
  // ISO "2026-06-03" → European "03.06.2026"
  const day = when ? String(when).slice(0, 10).split("-").reverse().join(".") : "";
  const subtitle = [
    day,
    source
      ? intl.formatMessage(
          { id: "service.docItemRow.sourceLabel", defaultMessage: "Source: {source}" },
          { source },
        )
      : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      id={idPrefix}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-state-mark-position="start"
      className={`group box-flat relative flex items-center gap-3 bg-page px-4 py-3 transition hover:bg-[color-mix(in_srgb,var(--color-page),var(--context-ink)_6%)] ${
        selected ? `ring-2 ring-accent-decoration ${onToggleSelect ? "" : "smycles-selected"}` : ""
      } ${
        draggable ? "cursor-grab active:cursor-grabbing" : ""
      }`}
    >
      {/* Selection checkbox: hidden at rest on hover-capable devices, revealed
          on row hover or when checked. Touch devices (no hover) always show it
          via `pointer-coarse:opacity-100`. */}
      {onToggleSelect && <input
        id={idPrefix && `${idPrefix}-select`}
        type="checkbox"
        disabled={disabled}
        checked={selected}
        onChange={onToggleSelect}
        aria-label={intl.formatMessage(
          { id: "service.docItemRow.selectAriaLabel", defaultMessage: "Select {name}" },
          { name },
        )}
        className={`h-5 w-5 shrink-0 cursor-pointer accent-accent transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-ui ${
          selected
            ? "opacity-100"
            : "opacity-0 group-hover:opacity-100 focus:opacity-100 pointer-coarse:opacity-100"
        }`}
      />}
      <span className="smycles-field-label relative flex h-8 w-8 shrink-0 items-center justify-center rounded bg-ink/[0.06] font-medium uppercase tracking-wider text-ink ring-1 ring-ink/5">
        {kind}
        {isNew ? (
          <span
            title={intl.formatMessage({ id: "service.docItemRow.newBadge", defaultMessage: "New" })}
            className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-accent"
            aria-hidden="true"
          />
        ) : null}
      </span>
      <div className="min-w-0 flex-1">
        {isRenaming ? (
          <>
            <ExtRenameInput
              id={idPrefix && `${idPrefix}-rename`}
              name={name}
              onRename={onRename}
              onBlur={onStopRename}
              autoFocus
              className={`smycles-field-meta w-full min-w-0 rounded-md bg-surface px-2 py-1 ${isNew ? "font-semibold" : "font-medium"} text-ink ring-1 ring-ink/15 transition focus:outline-none focus:ring-ink-ui`}
            />
            <div className="smycles-field-label mt-0.5 truncate text-ink">{sourceDisplay ? day : subtitle}</div>
          </>
        ) : (
          <>
            {onStartRename ? <button
              id={idPrefix && `${idPrefix}-name`}
              type="button"
              disabled={disabled}
              onClick={onStartRename}
              className={`smycles-field-meta block w-full truncate text-left ${isNew ? "font-semibold" : "font-medium"} transition hover:text-ink-accent`}
              title={intl.formatMessage({ id: "service.docItemRow.rename", defaultMessage: "Click to rename" })}
            >
              {name}
            </button> : <span id={idPrefix && `${idPrefix}-name`} className={`smycles-field-meta block truncate ${isNew ? "font-semibold" : "font-medium"}`}>{name}</span>}
            <div className="smycles-field-label truncate text-ink">{sourceDisplay ? day : subtitle}</div>
          </>
        )}
        {sourceDisplay && <div className="mt-1">{sourceDisplay}</div>}
      </div>
      <div className="flex shrink-0 items-center gap-3">
        {onDownload ? (
          <Button
            id={idPrefix && `${idPrefix}-download`}
            variant="icon"
            size="sm"
            disabled={disabled}
            onClick={onDownload}
            aria-label={intl.formatMessage({
              id: "service.docItemRow.download",
              defaultMessage: "Download",
            })}
            title={intl.formatMessage({
              id: "service.docItemRow.download",
              defaultMessage: "Download",
            })}
          >
            <DownloadIcon />
          </Button>
        ) : null}
        <Button
          id={idPrefix && `${idPrefix}-open`}
          variant="icon"
          size="sm"
          disabled={disabled}
          onClick={onOpen}
          aria-label={intl.formatMessage({ id: "service.docItemRow.open", defaultMessage: "Open" })}
          title={intl.formatMessage({ id: "service.docItemRow.open", defaultMessage: "Open" })}
        >
          <ArrowOpenIcon />
        </Button>
      </div>
    </div>
  );
}
